import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, uploadPhoto, updateProfile } from '../../lib/supabase';

const ProfilePage = () => {
  const { user, profile, refreshProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    bio: '',
    city: '',
    country: '',
    interests: [] as string[],
  });
  const [newInterest, setNewInterest] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        bio: profile.bio || '',
        city: profile.city || '',
        country: profile.country || '',
        interests: profile.interests || [],
      });
      setPhotos(profile.photos || []);
    }
  }, [profile]);

  const calculateAge = (birthDate: string) => {
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !user) return;

    const file = e.target.files[0];
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Dosya boyutu çok büyük. Maksimum 5MB olmalıdır.');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Lütfen bir resim dosyası seçin.');
      return;
    }

    setUploading(true);
    const { data: photoUrl, error } = await uploadPhoto(user.id, file);

    if (!error && photoUrl) {
      const newPhotos = [...photos, photoUrl];
      setPhotos(newPhotos);
      
      // Update profile with new photos
      await updateProfile(user.id, { photos: newPhotos });
      await refreshProfile();
    } else {
      alert('Fotoğraf yüklenirken bir hata oluştu.');
    }
    setUploading(false);
  };

  const handleDeletePhoto = async (photoUrl: string) => {
    if (!user) return;
    
    const newPhotos = photos.filter(p => p !== photoUrl);
    setPhotos(newPhotos);
    
    await updateProfile(user.id, { photos: newPhotos });
    await refreshProfile();
  };

  const handleAddInterest = () => {
    if (newInterest.trim() && !formData.interests.includes(newInterest.trim())) {
      setFormData(prev => ({
        ...prev,
        interests: [...prev.interests, newInterest.trim()]
      }));
      setNewInterest('');
    }
  };

  const handleRemoveInterest = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.filter(i => i !== interest)
    }));
  };

  const handleSave = async () => {
    if (!user) return;

    const { error } = await updateProfile(user.id, formData);
    
    if (!error) {
      await refreshProfile();
      setEditing(false);
    } else {
      alert('Profil güncellenirken bir hata oluştu.');
    }
  };

  if (!user || !profile) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Navbar />
        <div className="text-center pt-20">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Giriş Yapmanız Gerekiyor</h2>
          <p className="text-gray-600 mb-6">Profilinizi görüntülemek için lütfen giriş yapın</p>
          <button
            onClick={() => window.REACT_APP_NAVIGATE('/kayit')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Giriş Yap / Kayıt Ol
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      <Navbar />
      
      <div className="pt-20 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-display font-bold text-gray-900">Profilim</h1>
            <button
              onClick={() => editing ? handleSave() : setEditing(true)}
              className="px-6 py-3 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              {editing ? 'Kaydet' : 'Düzenle'}
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Left Column - Photos */}
            <div className="md:col-span-1">
              <div className="bg-white rounded-3xl shadow-xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Fotoğraflar</h2>
                
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <div className="w-full h-32">
                        <img
                          src={photo}
                          alt={`Fotoğraf ${index + 1}`}
                          className="w-full h-full object-cover object-top rounded-lg"
                        />
                      </div>
                      {editing && (
                        <button
                          onClick={() => handleDeletePhoto(photo)}
                          className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {editing && photos.length < 6 && (
                  <label className="block w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-center text-sm text-gray-600 hover:border-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
                    {uploading ? (
                      <span>Yükleniyor...</span>
                    ) : (
                      <>
                        <i className="ri-add-line text-xl"></i>
                        <span className="block mt-1">Fotoğraf Ekle</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                )}

                <p className="text-xs text-gray-500 mt-2 text-center">
                  Maksimum 6 fotoğraf ekleyebilirsiniz
                </p>
              </div>
            </div>

            {/* Right Column - Info */}
            <div className="md:col-span-2 space-y-6">
              {/* Basic Info */}
              <div className="bg-white rounded-3xl shadow-xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Temel Bilgiler</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ad Soyad</label>
                    {editing ? (
                      <input
                        type="text"
                        value={formData.full_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                        className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    ) : (
                      <p className="text-gray-900">{profile.full_name}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Yaş</label>
                      <p className="text-gray-900">{calculateAge(profile.birth_date)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Cinsiyet</label>
                      <p className="text-gray-900">{profile.gender}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Şehir</label>
                      {editing ? (
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      ) : (
                        <p className="text-gray-900">{profile.city || '-'}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ülke</label>
                      {editing ? (
                        <input
                          type="text"
                          value={formData.country}
                          onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      ) : (
                        <p className="text-gray-900">{profile.country || '-'}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bio */}
              <div className="bg-white rounded-3xl shadow-xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Hakkımda</h2>
                {editing ? (
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                    rows={4}
                    maxLength={500}
                    placeholder="Kendinizden bahsedin..."
                    className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  />
                ) : (
                  <p className="text-gray-700">{profile.bio || 'Henüz bir açıklama eklenmemiş.'}</p>
                )}
                {editing && (
                  <p className="text-xs text-gray-500 mt-2">{formData.bio.length}/500 karakter</p>
                )}
              </div>

              {/* Interests */}
              <div className="bg-white rounded-3xl shadow-xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">İlgi Alanları</h2>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {formData.interests.map((interest, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full flex items-center gap-2"
                    >
                      {interest}
                      {editing && (
                        <button
                          onClick={() => handleRemoveInterest(interest)}
                          className="w-4 h-4 flex items-center justify-center hover:bg-primary-200 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      )}
                    </span>
                  ))}
                </div>

                {editing && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newInterest}
                      onChange={(e) => setNewInterest(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddInterest()}
                      placeholder="İlgi alanı ekle..."
                      className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <button
                      onClick={handleAddInterest}
                      className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Ekle
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProfilePage;
